import PropTypes from 'prop-types';
import styles from './StreamField.module.scss';
import classNames from 'classnames';
import { snakeToPascal } from '../../utils/caseconverters';

import LineChartBlock from '../LineChartBlock';
import ColumnChartBlock from '../ColumnChartBlock';
import BarChartBlock from '../BarChartBlock';
import CandidateBlock from '../CandidateBlock/CandidateBlock';
import TextBlock from '../TextBlock/TextBlock';
import EditorialAccordionBlock from '../EditorialAccordionBlock/EditorialAccordionBlock';
import StackedAreaChartBlock from '../StackedAreaChartBlock/StackedAreaChartBlock';
import PolarChartBlock from '../PolarChartBlock';
import Richtext from '../Richtext';
import ImageBlock from '../ImageBlock';
import ImageGalleryBlock from '../ImageGalleryBlock';
import TreeMapChartBlock from '../TreeMapChartBlock/TreeMapChartBlock';
import TableBlock from '../Table/Table';
import ImageRichText from '../ImageRichText/ImageRichText';
import TimelineChartBlock from '../TimelineChartBlock';
import MixedChartBlock from '../MixedChartBlock';
import FenixExpandableTextValue from '../FenixExpandableTextValue';
import FenixTextValue from '../FenixTextValue';
import FenixSingleDataValue from '../FenixSingleDataValue';
import HeatMapBlock from '../HeatMapBlock';

const Components = {
    LineChartBlock,
    ColumnChartBlock,
    BarChartBlock,
    StackedAreaChartBlock,
    PolarChartBlock,
    TreeMapChartBlock,
    CandidateBlock,
    TextBlock,
    WatchtowerTextBlock: TextBlock,
    AccordionBlock: EditorialAccordionBlock,
    Richtext,
    Image: ImageBlock,
    ImageGallery: ImageGalleryBlock,
    Table: TableBlock,
    ImageRichText,
    FenixLineChartBlock: LineChartBlock,
    FenixColumnChartBlock: ColumnChartBlock,
    FenixBarChartBlock: BarChartBlock,
    FenixStackedAreaChartBlock: StackedAreaChartBlock,
    FenixPolarChartBlock: PolarChartBlock,
    FenixTreeMapChartBlock: TreeMapChartBlock,
    FenixTimelineChartBlock: TimelineChartBlock,
    FenixMixedChartBlock: MixedChartBlock,
    FenixHeatMapChartBlock: HeatMapBlock,
    FenixExpandableTextValueBlock: FenixExpandableTextValue,
    FenixTextValueBlock: FenixTextValue,
    FenixSingleDataValueBlock: FenixSingleDataValue,
    TimelineChartBlock,
    MixedChartBlock,
    HeatMapChartBlock: HeatMapBlock,
};

// Report comments can't be left on charts, which are redrawn, or on Fenix
// data blocks, whose text changes with the data.
const NOT_COMMENTABLE_BLOCKS = new Set([
    LineChartBlock,
    ColumnChartBlock,
    BarChartBlock,
    StackedAreaChartBlock,
    PolarChartBlock,
    TreeMapChartBlock,
    TimelineChartBlock,
    MixedChartBlock,
    HeatMapBlock,
    FenixExpandableTextValue,
    FenixTextValue,
    FenixSingleDataValue,
]);

const StreamField = ({
    items = [],
    noWrapper = false,
    itemGridSpan = 1,
    smallPadding,
    componentModifier,
}) => {
    if (!items.length) {
        return <></>;
    }

    const dynamicComponents = items.map((item, index) => {
        if (item.type) {
            item.type = snakeToPascal(item.type);
        } else if (item.wysiwyg) {
            item.type = 'Wysiwyg';
        }

        const Component =
            item.type in Components ? Components[item.type] : null;
        if (!Component) {
            return <></>;
        }

        const gridSpan =
            typeof item.value?.gridSpan !== 'undefined'
                ? item.value.gridSpan
                : itemGridSpan;

        const streamFieldCompCls = classNames(
            styles['StreamField__Component'],
            styles['StreamField__Component--' + item.type],
            {
                [styles[`StreamField__Component--ItemGridSpan-${gridSpan}`]]:
                    !Number.isNaN(gridSpan),
            },
            { [styles['StreamField__Component--SmallPadding']]: smallPadding }
        );

        const extraProps = componentModifier
            ? { modifier: componentModifier }
            : {};

        return (
            <div
                key={index}
                id={item.id}
                className={streamFieldCompCls}
                data-not-commentable={
                    NOT_COMMENTABLE_BLOCKS.has(Component) || undefined
                }>
                <Component {...item.value} {...extraProps} />
            </div>
        );
    });

    if (noWrapper) {
        return <>{dynamicComponents}</>;
    }

    return <div className={styles.StreamField}>{dynamicComponents}</div>;
};

StreamField.propTypes = {
    items: PropTypes.array,
    noWrapper: PropTypes.bool,
    itemGridSpan: PropTypes.number,
};

export default StreamField;
